import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PreviewPanel = ({ widgets, pressKitData, onSave, onPublish, onGenerateLink }) => {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [isPublished, setIsPublished] = useState(false);
  const [shareableLink, setShareableLink] = useState('');

  const previewModes = [
  { id: 'desktop', name: 'Desktop', icon: 'Monitor', width: '100%' },
  { id: 'tablet', name: 'Tablet', icon: 'Tablet', width: '768px' },
  { id: 'mobile', name: 'Mobile', icon: 'Smartphone', width: '375px' }];


  const handlePublish = async () => {
    try {
      // Simulate publish process
      const link = `https://presskit.pengamepublishing.com/${pressKitData?.slug || 'new-press-kit'}`;
      setShareableLink(link);
      setIsPublished(true);
      if (onPublish) {
        onPublish(link);
      }
    } catch (error) {
      console.error('Error publishing press kit:', error);
    }
  };

  const handleGenerateLink = () => {
    const link = `https://presskit.pengamepublishing.com/preview/${Date.now()}`;
    setShareableLink(link);
    if (onGenerateLink) {
      onGenerateLink(link);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard?.writeText(text);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const renderPreviewContent = () => {
    if (widgets?.length === 0) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Eye" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Preview Your Press Kit</h3>
            <p className="text-muted-foreground max-w-sm">
              Add widgets to the canvas to see how your press kit will look to visitors.
            </p>
          </div>
        </div>);

    }

    return (
      <div className="space-y-6">
        {/* Press Kit Header */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-start space-x-4">
            <Image
              src="https://images.unsplash.com/photo-1644060473538-dbbe07fd5195"
              alt="Book cover featuring elegant typography and artistic design elements"
              className="w-20 h-30 rounded-lg object-cover flex-shrink-0" />

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {pressKitData?.title || 'Press Kit Title'}
              </h1>
              <p className="text-muted-foreground mb-4">
                {pressKitData?.subtitle || 'Professional press materials and media resources'}
              </p>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>Published by PenGame Publishing</span>
                <span>•</span>
                <span>Updated {new Date()?.toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
        {/* Rendered Widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {widgets?.map((widget) =>
          <div key={widget?.id} className="bg-card rounded-lg border border-border p-4">
              {renderWidgetPreview(widget)}
            </div>
          )}
        </div>
        {/* Contact Information */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Media Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-foreground mb-2">Press Inquiries</h4>
              <p className="text-sm text-muted-foreground mb-1">Sarah Johnson</p>
              <p className="text-sm text-muted-foreground mb-1">PR Manager</p>
              <p className="text-sm text-muted-foreground mb-1">press@pengamepublishing.com</p>
              <p className="text-sm text-muted-foreground">(555) 123-4567</p>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">Review Copies</h4>
              <p className="text-sm text-muted-foreground mb-1">Michael Chen</p>
              <p className="text-sm text-muted-foreground mb-1">Marketing Director</p>
              <p className="text-sm text-muted-foreground mb-1">reviews@pengamepublishing.com</p>
              <p className="text-sm text-muted-foreground">(555) 123-4568</p>
            </div>
          </div>
        </div>
      </div>);

  };

  const renderWidgetPreview = (widget) => {
    switch (widget?.type) {
      case 'author-bio':
        return (
          <div className="flex space-x-4">
            <Image
              src={widget?.content?.image}
              alt={widget?.content?.imageAlt}
              className="w-16 h-16 rounded-full object-cover flex-shrink-0" />

            <div className="flex-1">
              <h4 className="font-semibold text-foreground mb-2">{widget?.content?.name}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">{widget?.content?.bio}</p>
            </div>
          </div>);


      case 'book-synopsis':
        return (
          <div>
            <h4 className="font-semibold text-foreground mb-2">{widget?.content?.title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{widget?.content?.synopsis}</p>
            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
              <span>{widget?.content?.genre}</span>
              <span>•</span>
              <span>{widget?.content?.pages} pages</span>
              <span>•</span>
              <span>ISBN: {widget?.content?.isbn}</span>
            </div>
          </div>);


      case 'download-section':
        return (
          <div>
            <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Download" size={16} />
              <span>{widget?.content?.title}</span>
            </h4>
            <div className="space-y-2">
              {widget?.content?.files?.map((file, index) =>
              <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={16} />
                    <span className="text-sm font-medium">{file?.name}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                    <span>{file?.type}</span>
                    <span>•</span>
                    <span>{file?.size}</span>
                    <button className="text-primary hover:text-primary/80">
                      <Icon name="Download" size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>);


      default:
        return (
          <div className="text-center py-8">
            <Icon name="Package" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{widget?.name}</p>
          </div>);

    }
  };

  return (
    <div className="w-96 bg-card border-l border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2 mb-4">
          <Icon name="Eye" size={20} />
          <span>Live Preview</span>
        </h2>

        {/* Preview Mode Selector */}
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          {previewModes?.map((mode) =>
          <button
            key={mode?.id}
            onClick={() => setPreviewMode(mode?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-medium transition-colors duration-200 ${
            previewMode === mode?.id ?
            'bg-card text-foreground shadow-sm' :
            'text-muted-foreground hover:text-foreground'}`
            }>

              <Icon name={mode?.icon} size={14} />
              <span>{mode?.name}</span>
            </button>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex space-x-2">
          <button
            onClick={onSave}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors duration-200">

            <Icon name="Save" size={16} />
            <span className="text-sm font-medium">Save Draft</span>
          </button>
          <button
            onClick={handlePublish}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200">

            <Icon name="Globe" size={16} />
            <span className="text-sm font-medium">Publish</span>
          </button>
        </div>

        {/* Shareable Link */}
        {(isPublished || shareableLink) &&
        <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Shareable Link</label>
            <div className="flex items-center space-x-2">
              <input
              type="text"
              value={shareableLink}
              readOnly
              className="flex-1 px-3 py-2 text-xs bg-muted border border-border rounded-md" />

              <button
              onClick={() => copyToClipboard(shareableLink)}
              className="p-2 bg-muted text-muted-foreground rounded-md hover:bg-muted/80 hover:text-foreground transition-colors duration-200">

                <Icon name="Copy" size={14} />
              </button>
            </div>
          </div>
        }

        {!shareableLink &&
        <button
          onClick={handleGenerateLink}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted transition-colors duration-200">

            <Icon name="Link" size={16} />
            <span className="text-sm font-medium">Generate Preview Link</span>
          </button>
        }
      </div>
      {/* Preview Content */}
      <div className="flex-1 overflow-y-auto">
        <div
          className="transition-all duration-300 mx-auto bg-background min-h-full"
          style={{
            width: previewModes?.find((m) => m?.id === previewMode)?.width,
            maxWidth: '100%'
          }}>

          <div className="p-6">
            {renderPreviewContent()}
          </div>
        </div>
      </div>
      {/* Status Bar */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${isPublished ? 'bg-success' : 'bg-warning'}`} />
            <span>{isPublished ? 'Published' : 'Draft'}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={12} />
            <span>Auto-saved {new Date()?.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </div>);

};

export default PreviewPanel;